import React from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const ChartContainer = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 24px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
`;

const CustomTooltip = styled.div`
  background-color: white;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TooltipLabel = styled.p`
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const TooltipItem = styled.p`
  margin: 4px 0;
  font-size: 13px;
  color: #666;
`;

const SessionChart = ({ sessions, successCriteria, repetitionCount }) => {
  // 세션 데이터가 없는 경우
  if (!sessions || sessions.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>세션별 성공률 추이</ChartTitle>
        <EmptyMessage>세션 기록이 없습니다.</EmptyMessage>
      </ChartContainer>
    );
  }

  // 목표 성공률 계산 (successCriteria는 횟수, repetitionCount는 총 시도 횟수)
  const targetSuccessRate = repetitionCount && successCriteria
    ? (successCriteria / repetitionCount) * 100
    : null;

  // 세션 데이터를 날짜 순으로 정렬하고 차트 데이터 형식으로 변환
  const chartData = sessions
    .map((session, index) => {
      const successCount = session.successCount || 0;
      const repetitionCount = session.repetitionCount || 1;
      const successRate = (successCount / repetitionCount) * 100;

      // 날짜 포맷팅
      let dateLabel = '';
      try {
        const date = session.sessionDate?.toDate
          ? session.sessionDate.toDate()
          : new Date(session.sessionDate);
        dateLabel = date.toLocaleDateString('ko-KR', {
          month: 'short',
          day: 'numeric'
        });
      } catch {
        dateLabel = `세션 ${index + 1}`;
      }

      return {
        name: dateLabel,
        sessionNumber: index + 1,
        successRate: Number(successRate.toFixed(1)),
        successCount,
        repetitionCount,
        date: session.sessionDate
      };
    })
    .sort((a, b) => {
      // 날짜로 정렬
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateA - dateB;
    })
    .map((item, index) => ({
      ...item,
      sessionNumber: index + 1,
      name: `세션 ${index + 1}`
    }));

  // 커스텀 툴팁 컴포넌트
  const CustomTooltipComponent = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <CustomTooltip>
          <TooltipLabel>{data.name}</TooltipLabel>
          <TooltipItem>성공률: {data.successRate}%</TooltipItem>
          <TooltipItem>
            성공: {data.successCount} / {data.repetitionCount}
          </TooltipItem>
        </CustomTooltip>
      );
    }
    return null;
  };

  return (
    <ChartContainer>
      <ChartTitle>세션별 성공률 추이</ChartTitle>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="name"
            stroke="#666"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            domain={[0, 100]}
            stroke="#666"
            style={{ fontSize: '12px' }}
            label={{ value: '성공률 (%)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltipComponent />} />
          <Legend
            wrapperStyle={{ fontSize: '14px' }}
            iconType="line"
          />

          {/* 성공 기준선 표시 */}
          {targetSuccessRate !== null && (
            <ReferenceLine
              y={targetSuccessRate}
              stroke="#28a745"
              strokeDasharray="5 5"
              label={{
                value: `목표: ${targetSuccessRate.toFixed(0)}%`,
                position: 'right',
                fill: '#28a745',
                fontSize: 12
              }}
            />
          )}

          <Line
            type="monotone"
            dataKey="successRate"
            stroke="#007bff"
            strokeWidth={3}
            dot={{ fill: '#007bff', r: 6 }}
            activeDot={{ r: 8 }}
            name="성공률 (%)"
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default SessionChart;
